import React, { FC } from 'react';
import AntModal from 'antd/lib/modal/Modal';
import { ModalProps } from 'antd/lib/modal/interface';
import { CloseIcon } from '../icons';
import { twMerge } from '@shared/utils/twMerge';
import { Button } from '../Button/Button';
import { CloseOutlined } from '@ant-design/icons';

export type CustomModalProps = {
  titleClassName?: string;
  okClassName?: string;
  hideCancel?: boolean;
  loading?: boolean;
  darkMode?: boolean;
  cancelClassName?: string;
  okProps?: React.ComponentProps<typeof Button>;
  cancelProps?: React.ComponentProps<typeof Button>;
  zIndex?: number;
  subtitle?: string;
};

export const Modal: FC<ModalProps & CustomModalProps> = props => {
  const { title, hideCancel, classNames, darkMode, ...restProps } = props;

  return (
    <AntModal
      transitionName=""
      centered={props.centered === undefined ? true : props.centered}
      className={twMerge(darkMode && 'text-white', props.className)}
      classNames={{
        ...classNames,
        header: twMerge(
          '!text-1824 text-center !mb-[32px]',
          darkMode && '!bg-darkCharcoal',
          classNames?.header
        ),

        content: twMerge(
          '!rounded-modal shadow-modalLight !p-[40px]',
          darkMode && '!bg-darkCharcoal [&_.ant-modal-close-icon]:!text-pastelBlue',

          classNames?.content
        ),
        mask: twMerge(classNames?.mask),
        body: 'font-nunito !text-1824',
        footer: twMerge(
          'flex justify-between pt-[7.5px] !mt-[32px] gap-[7.5px]',
          classNames?.footer
        ),
      }}
      title={
        <div className="flex flex-col gap-2">
          <h2
            className={twMerge(
              props.titleClassName ?? 'text-center',
              'text-1824 font-medium text-black',
              darkMode && 'text-white'
            )}
          >
            {title}
          </h2>
          {props.subtitle && (
            <p
              className={twMerge(
                props.titleClassName ?? 'text-center',
                'text-1824 font-nunito font-normal'
              )}
            >
              {props.subtitle}
            </p>
          )}
        </div>
      }
      footer={
        props.footer !== undefined
          ? props.footer
          : [
              ...(!hideCancel
                ? [
                    <Button
                      key={'cancel'}
                      onClick={props.onCancel}
                      darkMode={darkMode}
                      className={twMerge('min-w-[160px] rounded-[16px]', props.cancelClassName)}
                      data-testid={'cancel-modal-btn'}
                      {...props.cancelProps}
                    >
                      <span>{props.cancelText ?? 'Cancel'}</span>
                    </Button>,
                  ]
                : [<div key={'cancel'} />]),
              <Button
                key={'ok'}
                darkMode={darkMode}
                loading={props.loading}
                onClick={props.onOk}
                className={twMerge('min-w-[160px] rounded-[16px]', props.okClassName)}
                type="primary"
                data-testid={'ok-modal-btn'}
                {...props.okProps}
              >
                <span>{props.okText ?? 'Ok'}</span>
              </Button>,
            ]
      }
      closeIcon={
        props.closeIcon ? (
          <CloseIcon className="h-[7.5px] w-[7.5px]" />
        ) : (
          <CloseOutlined data-testid="closeButton" />
        )
      }
      {...restProps}
    >
      {props.children}
    </AntModal>
  );
};
